import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Heart, Plus, Users, HandHeart, Trash2, User, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PrayerWall() {
    const { user } = useAuth();
    const [prayers, setPrayers] = useState([]);
    const [myPrayers, setMyPrayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('community');
    
    // Form state
    const [title, setTitle] = useState('');
    const [request, setRequest] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchPrayers();
    }, []);

    const fetchPrayers = async () => {
        try {
            const [allPrayers, userPrayers] = await Promise.all([
                axios.get(`${API_URL}/prayers`),
                axios.get(`${API_URL}/prayers/mine`)
            ]);
            setPrayers(allPrayers.data);
            setMyPrayers(userPrayers.data);
        } catch (error) {
            toast.error('Failed to load prayer requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const response = await axios.post(`${API_URL}/prayers`, {
                title,
                request,
                is_anonymous: isAnonymous
            });
            
            setPrayers([response.data, ...prayers]);
            setMyPrayers([response.data, ...myPrayers]);
            toast.success('Prayer request shared with the community');
            resetForm();
            setIsDialogOpen(false);
        } catch (error) {
            toast.error('Failed to share prayer request');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePray = async (prayerId) => {
        try {
            const response = await axios.post(`${API_URL}/prayers/${prayerId}/pray`);
            
            // Update local state
            setPrayers(prayers.map(p => 
                p.id === prayerId 
                    ? { ...p, prayer_count: response.data.prayer_count, prayed_by: [...(p.prayed_by || []), user.id] }
                    : p
            ));
            
            toast.success(response.data.message);
        } catch (error) {
            toast.error('Failed to record prayer');
        }
    };

    const handleDelete = async (prayerId) => {
        try {
            await axios.delete(`${API_URL}/prayers/${prayerId}`);
            setPrayers(prayers.filter(p => p.id !== prayerId));
            setMyPrayers(myPrayers.filter(p => p.id !== prayerId));
            toast.success('Prayer request removed');
        } catch (error) {
            toast.error('Failed to delete prayer request');
        }
    };

    const resetForm = () => {
        setTitle('');
        setRequest('');
        setIsAnonymous(false);
    };

    const hasUserPrayed = (prayer) => {
        return prayer.prayed_by?.includes(user?.id);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    const PrayerCard = ({ prayer, showDelete = false }) => (
        <div className="verse-card animate-fade-in" data-testid={`prayer-card-${prayer.id}`}>
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {prayer.is_anonymous ? (
                            <User className="w-5 h-5 text-primary" />
                        ) : (
                            <span className="text-primary font-medium">
                                {prayer.user_name?.charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>
                    <div>
                        <p className="font-medium text-[#2D2A26]">
                            {prayer.is_anonymous ? 'Anonymous' : prayer.user_name}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(prayer.created_at), { addSuffix: true })}
                        </p>
                    </div>
                </div>
                {showDelete && prayer.user_id === user?.id && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(prayer.id)}
                        data-testid={`delete-prayer-${prayer.id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                )}
            </div>
            
            <h3 className="font-display text-lg text-[#2D2A26] mb-2">{prayer.title}</h3>
            <p className="text-muted-foreground leading-relaxed mb-6">{prayer.request}</p>
            
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <HandHeart className="w-4 h-4 text-primary" />
                    <span>{prayer.prayer_count} {prayer.prayer_count === 1 ? 'person' : 'people'} prayed</span>
                </div>
                
                <Button
                    variant={hasUserPrayed(prayer) ? "secondary" : "default"}
                    size="sm"
                    onClick={() => handlePray(prayer.id)}
                    disabled={hasUserPrayed(prayer)}
                    className={hasUserPrayed(prayer) ? "btn-secondary" : "btn-primary"}
                    data-testid={`pray-btn-${prayer.id}`}
                >
                    <Heart className={`w-4 h-4 mr-2 ${hasUserPrayed(prayer) ? 'fill-current' : ''}`} />
                    {hasUserPrayed(prayer) ? 'Prayed' : 'I Prayed'}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="prayer-wall-page">
            <Navigation />
            
            <main className="container-app py-8 md:py-12">
                {/* Header */}
                <div className="mb-10 animate-fade-in">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-6 h-6 text-primary" />
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                            Community
                        </span>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <h1 className="font-display text-3xl md:text-4xl font-medium text-[#2D2A26] mb-2">
                                Prayer Wall
                            </h1>
                            <p className="text-muted-foreground">
                                Share your prayer requests and lift up others in prayer
                            </p>
                        </div>
                        
                        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="new-prayer-btn">
                                    <Plus className="w-5 h-5 mr-2" />
                                    Share Prayer Request
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-display text-2xl">Share a Prayer Request</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="title">Title</Label>
                                        <Input
                                            id="title"
                                            data-testid="prayer-title-input"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder="Brief title for your request"
                                            required
                                            className="bg-white/50"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="request">Your Prayer Request</Label>
                                        <Textarea
                                            id="request"
                                            data-testid="prayer-request-input"
                                            value={request}
                                            onChange={(e) => setRequest(e.target.value)}
                                            placeholder="Share what you'd like the community to pray for..."
                                            required
                                            rows={5}
                                            className="bg-white/50 resize-none"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                                        <div>
                                            <Label htmlFor="anonymous" className="font-medium">Post Anonymously</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Your name won't be shown to others
                                            </p>
                                        </div>
                                        <Switch
                                            id="anonymous"
                                            data-testid="anonymous-switch"
                                            checked={isAnonymous}
                                            onCheckedChange={setIsAnonymous}
                                        />
                                    </div>

                                    <div className="flex gap-3 justify-end pt-4">
                                        <Button 
                                            type="button" 
                                            variant="ghost"
                                            onClick={() => setIsDialogOpen(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            className="btn-primary"
                                            disabled={submitting}
                                            data-testid="submit-prayer-btn"
                                        >
                                            {submitting ? 'Sharing...' : 'Share Request'}
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Verse Banner */}
                <div className="glass rounded-2xl p-6 mb-10 text-center animate-fade-in">
                    <p className="font-accent text-lg text-[#2D2A26] italic">
                        "Bear ye one another's burdens, and so fulfil the law of Christ."
                    </p>
                    <p className="text-sm text-primary mt-2">— Galatians 6:2 (KJV)</p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-muted/50">
                        <TabsTrigger value="community" data-testid="community-tab">
                            <Users className="w-4 h-4 mr-2" />
                            Community ({prayers.length})
                        </TabsTrigger>
                        <TabsTrigger value="mine" data-testid="my-prayers-tab">
                            <Heart className="w-4 h-4 mr-2" />
                            My Requests ({myPrayers.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="community" className="space-y-6">
                        {prayers.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                                    <HandHeart className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-display text-xl text-[#2D2A26] mb-2">
                                    No Prayer Requests Yet
                                </h3>
                                <p className="text-muted-foreground mb-6">
                                    Be the first to share a prayer request with the community
                                </p>
                                <Button 
                                    onClick={() => setIsDialogOpen(true)}
                                    className="btn-primary"
                                >
                                    Share First Request
                                </Button>
                            </div>
                        ) : (
                            <div className="grid gap-6 md:grid-cols-2">
                                {prayers.map((prayer, index) => (
                                    <div key={prayer.id} className={`stagger-${Math.min(index + 1, 6)}`}>
                                        <PrayerCard prayer={prayer} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="mine" className="space-y-6">
                        {myPrayers.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                                    <Heart className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-display text-xl text-[#2D2A26] mb-2">
                                    No Prayer Requests
                                </h3>
                                <p className="text-muted-foreground mb-6">
                                    Share what's on your heart with the community
                                </p>
                                <Button 
                                    onClick={() => setIsDialogOpen(true)}
                                    className="btn-primary"
                                >
                                    Share a Request
                                </Button>
                            </div>
                        ) : (
                            <div className="grid gap-6 md:grid-cols-2">
                                {myPrayers.map((prayer, index) => (
                                    <div key={prayer.id} className={`stagger-${Math.min(index + 1, 6)}`}>
                                        <PrayerCard prayer={prayer} showDelete />
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
